import * as React from 'react'
import { useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'

interface TocEntry {
  id: string
  text: string
}

function collectHeadings(): TocEntry[] {
  const main = document.querySelector('main')
  if (!main) return []
  return Array.from(main.querySelectorAll('h2[id]')).reduce<TocEntry[]>((acc, el) => {
    const id = (el as HTMLElement).id
    const text = el.textContent?.trim() ?? ''
    if (id && text) acc.push({ id, text })
    return acc
  }, [])
}

export function TableOfContents() {
  const { pathname } = useLocation()
  const [entries, setEntries] = React.useState<TocEntry[]>([])
  const [activeId, setActiveId] = React.useState<string>('')

  // Re-collect headings whenever the route changes (after paint)
  React.useEffect(() => {
    const timer = setTimeout(() => {
      const collected = collectHeadings()
      setEntries(collected)
      setActiveId(collected[0]?.id ?? '')
    }, 80)
    return () => clearTimeout(timer)
  }, [pathname])

  // Track active section via IntersectionObserver
  React.useEffect(() => {
    if (entries.length === 0) return

    const observer = new IntersectionObserver(
      (observed) => {
        const visible = observed.filter((e) => e.isIntersecting)
        if (visible.length === 0) return
        const topmost = visible.reduce((prev, curr) =>
          prev.boundingClientRect.top <= curr.boundingClientRect.top ? prev : curr,
        )
        setActiveId((topmost.target as HTMLElement).id)
      },
      { rootMargin: '0px 0px -60% 0px', threshold: 0 },
    )

    entries.forEach(({ id }) => {
      const el = document.getElementById(id)
      if (el) observer.observe(el)
    })

    return () => observer.disconnect()
  }, [entries])

  if (entries.length === 0) return null

  return (
    <nav
      aria-label="On this page"
      className="hidden lg:block w-48 shrink-0 sticky top-6 self-start pl-4"
    >
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-3">
        On this page
      </p>
      <ul className="space-y-2">
        {entries.map(({ id, text }) => (
          <li key={id}>
            <a
              href={`#${id}`}
              onClick={(e) => {
                e.preventDefault()
                const el = document.getElementById(id)
                if (el) {
                  el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  setActiveId(id)
                }
              }}
              className={cn(
                'block text-[13px] leading-snug transition-colors',
                activeId === id
                  ? 'text-foreground font-medium'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  )
}
